import { cn } from '../../../lib/utils';
import { CheckCircle2, Circle, MapPin, Clock } from 'lucide-react';

export interface TrackingEvent {
    status: string;
    description: string;
    location: string;
    date: string;
    time: string;
    isCompleted: boolean;
    isCurrent?: boolean;
}

interface ShipmentTrackingProps {
    events: TrackingEvent[];
    className?: string;
}

export const ShipmentTracking = ({ events, className }: ShipmentTrackingProps) => {
    return (
        <div className={cn("flex flex-col space-y-0", className)}>
            {events.map((event, index) => {
                const isLast = index === events.length - 1;
                
                return (
                    <div key={index} className="flex group min-h-[100px]">
                        {/* Timeline rail */}
                        <div className="flex flex-col items-center mr-4 sm:mr-8">
                            <div 
                                className={cn(
                                    "size-6 rounded-full flex items-center justify-center z-10 transition-all duration-500",
                                    event.isCompleted ? "bg-primary text-white scale-110 shadow-lg shadow-primary/20" : 
                                    event.isCurrent ? "bg-white border-2 border-primary text-primary animate-pulse" : 
                                    "bg-white border-2 border-gray-200 text-gray-300"
                                )}
                            >
                                {event.isCompleted ? (
                                    <CheckCircle2 className="size-3.5" />
                                ) : (
                                    <Circle className="size-2 fill-current" />
                                )}
                            </div>
                            
                            {!isLast && (
                                <div 
                                    className={cn(
                                        "w-0.5 h-full -mt-1 -mb-1 border-l-2",
                                        event.isCompleted ? "border-primary border-solid" : "border-gray-200 border-dashed"
                                    )} 
                                />
                            )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 pb-10">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-2">
                                <div className="flex flex-col">
                                    <span className={cn(
                                        "text-xs font-black uppercase tracking-widest mb-1",
                                        event.isCompleted ? "text-primary" : "text-gray-400"
                                    )}>
                                        {event.date}
                                    </span>
                                    <h4 className={cn(
                                        "text-lg font-black tracking-tight leading-none",
                                        event.isCompleted || event.isCurrent ? "text-secondary" : "text-gray-400"
                                    )}>
                                        {event.status}
                                    </h4>
                                </div>
                                <div className="flex items-center gap-2 text-primary/60 sm:text-right">
                                    <Clock className="size-3" />
                                    <span className="text-sm font-black tracking-tighter">{event.time}</span>
                                </div>
                            </div>
                            
                            <p className={cn(
                                "text-sm font-medium leading-relaxed mb-3 transition-colors",
                                event.isCompleted || event.isCurrent ? "text-slate-600" : "text-gray-400"
                            )}>
                                {event.description}
                            </p>
                            
                            {event.location && (
                                <div className={cn(
                                    "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all",
                                    event.isCompleted || event.isCurrent ? "bg-slate-100 text-slate-500 ring-1 ring-slate-200" : "bg-gray-50 text-gray-300 ring-1 ring-gray-100"
                                )}>
                                    <MapPin className="size-3" />
                                    {event.location}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
